import type { ReactNode } from 'react';

import { cn } from '@shigo-ui/ui';

import { DASHBOARD_TABLE_HEADER_HEIGHT_PX } from './DashboardDataTable.constants';
import { useScrollbarWidth } from './useScrollbarWidth';

type DashboardDataTableFillHeightScrollProps = {
  children: ReactNode;
  headerHeightPx?: number | undefined;
  maxHeightPx?: number | undefined;
};

/**
 * Scroll container for fill-height tables with sticky header support.
 *
 * Renders two overlay elements outside the scroll region:
 * - Scrollbar mask: covers the vertical scrollbar track in the header area
 * - Header border: fixed 1px line at the bottom of the pinned header row
 */
export function DashboardDataTableFillHeightScroll({
  children,
  headerHeightPx = DASHBOARD_TABLE_HEADER_HEIGHT_PX,
  maxHeightPx,
}: DashboardDataTableFillHeightScrollProps) {
  const scrollbarWidth = useScrollbarWidth();

  return (
    <div className={cn('relative min-h-0', maxHeightPx === undefined && 'flex-1')}>
      {scrollbarWidth > 0 ? (
        <div
          className="bg-card pointer-events-none absolute top-0 right-0 z-50"
          style={{ width: scrollbarWidth, height: headerHeightPx }}
          aria-hidden="true"
        />
      ) : null}
      <div
        className="bg-border pointer-events-none absolute right-0 left-0 z-50 h-px"
        style={{ top: headerHeightPx }}
        aria-hidden="true"
      />
      <div
        className={cn('overflow-auto', maxHeightPx === undefined && 'h-full')}
        style={maxHeightPx === undefined ? undefined : { maxHeight: maxHeightPx }}
      >
        {children}
      </div>
    </div>
  );
}
